import './App.css';
import { lazy, Suspense, useEffect } from 'react';
import {Navigate, Route, Routes} from "react-router-dom";
import {ProtectedRoute} from "./Components/ProtectedRoute/ProtectedRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./Components/users/common/Toast/Toast";
import Landing from "./Components/public/Landing/Landing";
import UserService from "./Service/UserService";
import TokenService from "./Service/TokenService";
import PushNotificationService from "./Service/PushNotificationService";
import PwaPushBanner from "./Components/PwaPushBanner/PwaPushBanner";
import PwaUpdateBanner from "./Components/PwaUpdateBanner/PwaUpdateBanner";
import SubscriptionExpiryBanner from "./Components/SubscriptionExpiryBanner/SubscriptionExpiryBanner";

// The landing is the entry page for new visitors, so it stays in the main bundle.
// Everything else (cabinet with antd, calendar, pdf-lib) loads on demand.
const CalendarPage = lazy(() => import("./Components/users/Dashboard/CalendarPage"));
const ClientPage = lazy(() => import("./Components/users/Client/ClientPage"));
const TemplatesPage = lazy(() => import("./Components/users/Templates/TemplatesPage"));
const ServicesPage = lazy(() => import("./Components/users/Settings/SubItems/Services/ServicesPage"));
const CalendarSettingsPage = lazy(() => import("./Components/users/Settings/SubItems/CalendarSettings/CalendarSettingsPage"));
const OnlineBookingPage = lazy(() => import("./Components/users/Settings/SubItems/OnlineBooking/OnlineBookingPage"));
const NotificationsPage = lazy(() => import("./Components/users/Settings/SubItems/Notifications/NotificationsPage"));
const SmsPage = lazy(() => import("./Components/users/Sms/SmsPage"));
const CompanyProfilePage = lazy(() => import("./Components/users/Settings/SubItems/CompanyProfile/CompanyProfilePage"));
const MastersPage = lazy(() => import("./Components/users/Settings/SubItems/Masters/MastersPage"));
const TariffsPage = lazy(() => import("./Components/users/Settings/SubItems/Tariffs/TariffsPage"));
const AssistantPage = lazy(() => import("./Components/users/Settings/SubItems/Assistant/AssistantPage"));
const InstagramCallbackPage = lazy(() => import("./Components/users/Settings/SubItems/Assistant/InstagramCallbackPage"));
const BookingPage = lazy(() => import("./Components/public/BookingPage/BookingPage"));
const ConfirmPage = lazy(() => import("./Components/public/ConfirmPage/ConfirmPage"));
const Terms = lazy(() => import("./Components/public/Legal/Terms"));
const Privacy = lazy(() => import("./Components/public/Legal/Privacy"));
const DataDeletion = lazy(() => import("./Components/public/Legal/DataDeletion"));
const Login = lazy(() => import("./Components/users/Auth/Login"));
const Register = lazy(() => import("./Components/users/Auth/Register"));
const ForgotPassword = lazy(() => import("./Components/users/Auth/ForgotPassword"));
const NotFound = lazy(() => import("./Components/NotFound/NotFound"));
const PaymentResult = lazy(() => import("./Components/PaymentResult/PaymentResult"));

const RootRedirect = () =>
    UserService.isAuthenticated()
        ? <Navigate to="/calendar" replace />
        : <Navigate to="/landing" replace />;

function App() {
    useEffect(() => {
        if (localStorage.getItem('pwa_install_pending')) {
            const userId = TokenService.getUserId();
            if (userId) {
                PushNotificationService.reportPwaInstalled(userId);
                localStorage.removeItem('pwa_install_pending');
            }
        }
    }, []);

    // Umami web analytics — self-hosted, tracks the public landing (and, by
    // extension, the whole SPA). Only injected once both build-time env vars
    // are present: REACT_APP_UMAMI_WEBSITE_ID is empty until someone
    // manually registers the site in the Umami Dashboard after the infra
    // step is deployed, so an empty value here is an expected, non-error
    // state (no script tag should reach the DOM at all in that case).
    useEffect(() => {
        const scriptSrc = process.env.REACT_APP_UMAMI_SCRIPT_SRC;
        const websiteId = process.env.REACT_APP_UMAMI_WEBSITE_ID;
        if (!scriptSrc || !websiteId) {
            return;
        }
        const script = document.createElement('script');
        script.defer = true;
        script.src = scriptSrc;
        script.setAttribute('data-website-id', websiteId);
        document.head.appendChild(script);
    }, []);

    return (
        <ThemeProvider>
        {/* Cabinet toasts (useToast). Public pages never call it; the empty
            container renders nothing visible there. */}
        <ToastProvider>
        <PwaUpdateBanner />
        <PwaPushBanner />
        <SubscriptionExpiryBanner />
        <Suspense fallback={null}>
        <Routes>
            <Route path="/" element={<RootRedirect />}/>
            <Route path="/calendar" element={<ProtectedRoute element={<CalendarPage/>}/>}/>
            <Route path="/clients" element={<ProtectedRoute element={<ClientPage/>}/>}/>
            <Route path="/agreements" element={<ProtectedRoute element={<TemplatesPage/>}/>}/>
            <Route path="/settings/services" element={<ProtectedRoute element={<ServicesPage/>}/>}/>
            <Route path="/settings/worktime" element={<ProtectedRoute element={<CalendarSettingsPage/>}/>}/>
            <Route path="/settings/client" element={<ProtectedRoute element={<OnlineBookingPage/>}/>}/>
            <Route path="/settings/notifications" element={<ProtectedRoute element={<NotificationsPage/>}/>}/>
            <Route path="/sms" element={<ProtectedRoute element={<SmsPage/>}/>}/>
            <Route path="/settings/sms" element={<Navigate to="/sms" replace />}/>
            <Route path="/settings/company" element={<ProtectedRoute element={<CompanyProfilePage/>}/>}/>
            <Route path="/settings/masters" element={<ProtectedRoute element={<MastersPage/>}/>}/>
            <Route path="/settings/tariffs" element={<ProtectedRoute element={<TariffsPage/>}/>}/>
            <Route path="/settings/assistant" element={<ProtectedRoute element={<AssistantPage/>}/>}/>
            <Route path="/settings/instagram-callback" element={<ProtectedRoute element={<InstagramCallbackPage/>}/>}/>
            <Route path="/book" element={<BookingPage/>}/>
            <Route path="/book/:masterId" element={<BookingPage/>}/>
            <Route path="/c/:token" element={<ConfirmPage/>}/>
            <Route path="/payment/result" element={<PaymentResult/>}/>
            <Route path="/login" element={<Login/>}/>
            <Route path="/register" element={<Register/>}/>
            <Route path="/forgot-password" element={<ForgotPassword/>}/>
            <Route path="/landing" element={<Landing/>}/>
            <Route path="/terms" element={<Terms/>}/>
            <Route path="/privacy" element={<Privacy/>}/>
            <Route path="/data-deletion" element={<DataDeletion/>}/>
            <Route path="*" element={<NotFound/>}/>
        </Routes>
        </Suspense>
        </ToastProvider>
        </ThemeProvider>
    );
}

export default App;
